import React from 'react';
import { InputNumber, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CommercialQuantityEditor from '../orders/components/CommercialQuantityEditor';
import { formatUnit } from '../../utils/units';
import { formatConversionValue } from '../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../utils/polishNumberInput';
import { COMMERCIAL_UNITS } from '../../utils/commercialQuantity';
import {
  WAREHOUSE_QUANTITY_MIN,
  WAREHOUSE_QUANTITY_PRECISION,
  roundWarehouseQuantity,
} from '../../utils/warehouseQuantity';
import {
  WarehouseQuantityLine,
  WarehouseQuantityPatch,
  buildWarehouseQuantityPatch,
  buildWarehouseQuantityValue,
  getWarehouseLineUnit,
  supportsWarehouseConversion,
} from './warehouseQuantityConversion';

interface WarehouseQuantityFieldProps {
  line: WarehouseQuantityLine;
  onChange: (patch: WarehouseQuantityPatch) => void;
  maxQuantity: number;
  maxConvertedQuantity: number;
  disabled?: boolean;
  /** wz:conversion.noDimensions / pz:conversion.noDimensions — the caller owns its namespace. */
  noDimensionsLabel: string;
  unitLabel: string;
  /** Optional empty-draft presentation; an unknown amount is not zero stock. */
  emptyQuantityLabel?: string;
  pricingQuantity?: number | null;
  pricingUnit?: string | null;
  /** Keep supplier-order-traced PZ stock quantity fixed while allowing a commercial unit conversion. */
  physicalQuantityLocked?: boolean;
  /** Only WZ creation uses zero as an instruction to omit a not-yet-issued line. */
  onZero?: () => void;
}

/**
 * The quantity control on a WZ / PZ document line.
 *
 * Replaces the old "Ilość + Jednostka + Przelicznik" trio of loose controls with
 * the quantity plus the same "Przelicznik jednostek" modal the order lines
 * already use. That modal is bidirectional: the operator can enter pieces,
 * area or volume and select either remaining view. The old per-line Select
 * only ever computed one direction and showed the result as small grey text.
 *
 * Dimensional lines choose their transaction unit inside that same modal.
 * A catalog product never supplies or locks a unit; only a traced PZ amount
 * can be fixed to the supplier line's already-agreed physical quantity.
 */
const WarehouseQuantityField: React.FC<WarehouseQuantityFieldProps> = ({
  line,
  onChange,
  maxQuantity,
  maxConvertedQuantity,
  disabled = false,
  noDimensionsLabel,
  emptyQuantityLabel,
  pricingQuantity,
  pricingUnit,
  physicalQuantityLocked = false,
  onZero,
}) => {
  const { t, i18n } = useTranslation(['orders']);
  const lineUnit = getWarehouseLineUnit(line);
  const quantity = Number(line.quantity) || 0;

  // Legacy weight/length rows have no dimensional conversion. They remain
  // readable, but the active editor only offers szt./m²/m³.
  if (!supportsWarehouseConversion(line)) {
    return (
      <>
        <InputNumber
          value={quantity}
          onChange={(next) => {
            // Clearing an InputNumber briefly yields null while the operator
            // types a replacement value. Only an explicit numeric zero means
            // "omit this line"; an empty edit must not delete it by accident.
            if (onZero && next !== null && Number(next) === 0) {
              onZero();
              return;
            }
            onChange({
              quantity: roundWarehouseQuantity(next),
              conversionUnit: null,
              unit: lineUnit,
            });
          }}
          disabled
          min={onZero ? 0 : WAREHOUSE_QUANTITY_MIN}
          max={maxQuantity}
          step={WAREHOUSE_QUANTITY_MIN}
          precision={WAREHOUSE_QUANTITY_PRECISION}
          addonAfter={formatUnit(line.unit, i18n.language)}
          style={{ width: '100%' }}
          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
        />
        <Typography.Text type="warning" style={{ display: 'block', fontSize: 12 }}>
          {t('orders:unitConversion.legacyUnitReadOnly')}
        </Typography.Text>
      </>
    );
  }

  const conversionLabel = line.conversionUnit
    ? formatConversionValue(line.conversionUnit, { ...line, unit: lineUnit }, quantity)
    : '';

  return (
    <>
      <CommercialQuantityEditor
        value={buildWarehouseQuantityValue({
          ...line,
          ...(pricingQuantity === undefined ? {} : { pricingQuantity }),
          ...(pricingUnit === undefined ? {} : { pricingUnit }),
        })}
        onChange={(next) => onChange(buildWarehouseQuantityPatch(next))}
        maxPhysicalQuantity={maxQuantity}
        maxPricingQuantity={maxConvertedQuantity}
        physicalUnit={lineUnit}
        lockPhysicalQuantity={physicalQuantityLocked}
        pricingConversionUnits={COMMERCIAL_UNITS}
        disabled={disabled}
        compact
        triggerOnly={line.quantity == null && Boolean(emptyQuantityLabel)}
        triggerLabel={emptyQuantityLabel}
        allowZero={Boolean(onZero)}
        onZero={onZero}
        width={line.width}
        length={line.length}
        height={line.height}
        thickness={line.thickness}
      />
      {line.conversionUnit && !conversionLabel && (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {noDimensionsLabel}
        </Typography.Text>
      )}
    </>
  );
};

export default WarehouseQuantityField;
